
import { fetchFacebookNews, fetchInstagramNews, fetchTwitterNews, fetchYouTubeNews } from './newsAggregator';

interface SocialFeedConfig {
  id: string;
  platform: 'facebook' | 'instagram' | 'twitter' | 'youtube';
  accountId?: string;
  pageId?: string;
  hashtag?: string;
  enabled: boolean;
  refreshInterval: number; // minutes
}

interface SocialFeedArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  source: {
    name: string;
    logo: string;
    logoAlt: string;
    url: string;
  };
  category: string;
  timestamp: string;
  readTime: string;
  country: string;
  platform: string;
  engagement?: number;
  externalUrl?: string;
}

/**
 * Fetch social media feeds based on configuration
 */
export async function fetchSocialFeeds(
  configs: SocialFeedConfig[],
  country: string = 'WORLD'
): Promise<SocialFeedArticle[]> {
  const enabledConfigs = configs.filter(config => config.enabled);
  const allArticles: SocialFeedArticle[] = [];

  for (const config of enabledConfigs) {
    try {
      let articles: any[] = [];

      switch (config.platform) {
        case 'facebook':
          articles = await fetchFacebookNews(country);
          break;
        case 'instagram':
          articles = await fetchInstagramNews(country);
          break;
        case 'twitter':
          articles = await fetchTwitterNews(country, 'news');
          break;
        case 'youtube':
          articles = await fetchYouTubeNews(country, 'news');
          break;
      }

      allArticles.push(...articles);
    } catch (error) {
      console.error(`Error fetching ${config.platform} feed:`, error);
    }
  }

  // Sort by engagement or timestamp
  allArticles.sort((a, b) => {
    if (a.engagement && b.engagement) {
      return b.engagement - a.engagement;
    }
    return 0;
  });

  return allArticles;
}

/**
 * Default social feed configurations
 */
export const DEFAULT_SOCIAL_CONFIGS: SocialFeedConfig[] = [
  {
    id: 'facebook-news',
    platform: 'facebook',
    pageId: 'BBCNews',
    enabled: true,
    refreshInterval: 30,
  },
  {
    id: 'instagram-news',
    platform: 'instagram',
    enabled: true,
    refreshInterval: 30,
  },
  {
    id: 'twitter-news',
    platform: 'twitter',
    hashtag: 'news',
    enabled: true,
    refreshInterval: 15,
  },
  {
    id: 'youtube-news',
    platform: 'youtube',
    enabled: true,
    refreshInterval: 60,
  },
];

/**
 * Validate social media API credentials
 */
export function validateSocialCredentials(): {
  facebook: boolean;
  instagram: boolean;
  twitter: boolean;
  youtube: boolean;
} {
  return {
    facebook: !!(process.env.FACEBOOK_ACCESS_TOKEN && process.env.FACEBOOK_ACCESS_TOKEN !== 'your-facebook-token-here'),
    instagram: !!(process.env.INSTAGRAM_ACCESS_TOKEN && process.env.INSTAGRAM_ACCESS_TOKEN !== 'your-instagram-token-here'),
    twitter: !!(process.env.TWITTER_BEARER_TOKEN && process.env.TWITTER_BEARER_TOKEN !== 'your-twitter-bearer-token-here'),
    youtube: !!(process.env.YOUTUBE_API_KEY && process.env.YOUTUBE_API_KEY !== 'your-youtube-api-key-here'),
  };
}